<template>
  <div class="w-full">
    <div class="flex flex-col mt-2">
      <div class="flex flex-row justify-between items-center mt-2 mb-1">
        <button class="cursor-pointer px-3 py-1 text-gray-300 hover:text-white" @click="previous()">
          <font-awesome-icon :icon="['fas', 'chevron-left']"/>
        </button>
        <p class="text-xl text-gradient font-extrabold">{{ leaderboardDate }}</p>
        <button
          class="px-3 py-1"
          :class="numberOfDaysBack > 0 ? 'cursor-pointer text-gray-300 hover:text-white' : 'text-gray-700 cursor-default'"
          :disabled="numberOfDaysBack === 0"
          @click="next()">
          <font-awesome-icon :icon="['fas', 'chevron-right']"/>
        </button>
      </div>

      <button
        v-if="numberOfDaysBack > 0"
        @click="playThisDay"
        class="self-center mb-3 px-5 py-1.5 rounded bg-gradient-to-r from-green-400 to-teal-400 hover:bg-green-500 text-gray-900 font-semibold cursor-pointer">
        Play this day
      </button>

      <LeaderboardTable
        :entries="entries"
        :loading="loading"
        :current-user-id="currentUserId"
        :period-label="periodLabel"
        empty-text="No scores for this day yet."/>
    </div>
  </div>
</template>
<script setup>

import {computed, onMounted, ref} from "vue";
import LeaderboardTable from "./LeaderboardTable.vue";
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";
import {safeFetch} from "../utils/http.js";
import {useArchive} from "../composables/useArchive.js";
import {SERIES_LENGTH} from "../composables/gameConstants.js";

const {playDate} = useArchive();

// Backend FormattedDate form (UTC `yyyy-M-d`, no leading zeros) — the Words partition key,
// matching ScoreCalendar.vue's fmt(). Launches that day as a leaderboard-free practice run.
function playThisDay() {
  const d = new Date(Date.now() - numberOfDaysBack.value * 86400000);
  const date = `${d.getUTCFullYear()}-${d.getUTCMonth() + 1}-${d.getUTCDate()}`;
  playDate(date);
}

// Local state on purpose: this component is rendered both on the game-over
// screen and inside the stats modal, so it must not share a singleton (date
// navigation in one instance would otherwise mutate the other).
const scores = ref([]);
const loading = ref(false);

const currentUserId = localStorage.getItem('userId') || '';

let numberOfDaysBack = ref(0);

async function getScores(daysBack) {
  loading.value = true;
  try {
    const response = await safeFetch(`${import.meta.env.VITE_APP_API_BASE_URL}/api/scores?fromToday=${daysBack}`);
    if (response?.ok) {
      scores.value = await response.json();
    } else {
      console.error("Failed to fetch scores", response?.statusText);
    }
  } finally {
    loading.value = false;
  }
}

onMounted(async () => {
  await getScores(numberOfDaysBack.value);
})

const entries = computed(() => scores.value.map(s => ({
  id: s.id,
  name: s.name,
  verified: s.verified,
  valueDisplay: s.score,
  isPerfect: s.score === SERIES_LENGTH
})));

const periodLabel = computed(() => {
  if (numberOfDaysBack.value === 0) return 'today';
  if (numberOfDaysBack.value === 1) return 'yesterday';
  return `on ${leaderboardDate.value}`;
});

const leaderboardDate = computed(() => {
  if (numberOfDaysBack.value === 0) {
    return 'Today';
  } else if (numberOfDaysBack.value === 1) {
    return 'Yesterday';
  }
  let date = new Date();
  date.setDate(date.getDate() - numberOfDaysBack.value);
  let day = String(date.getDate()).padStart(2, '0');
  let month = String(date.getMonth() + 1).padStart(2, '0');
  let year = date.getFullYear();
  return `${day}/${month}/${year}`;
});

async function previous() {
  numberOfDaysBack.value++;
  await getScores(numberOfDaysBack.value);
}

async function next() {
  if (numberOfDaysBack.value <= 0) return;
  numberOfDaysBack.value--;
  await getScores(numberOfDaysBack.value);
}
</script>


<style scoped>

</style>
